import {
  TabProps,
  User,
} from "@/components/SettingsSidepanel/settings.types.ts";
import SettingsSheetHeader from "@/components/SettingsSidepanel/SettingsSheetHeader.tsx";
import SettingsSheetProfile from "@/components/SettingsSidepanel/SettingsSheetProfile.tsx";
import { Separator } from "@/components/ui/separator.tsx";

import { ProjectOfficesQuery, UsersQuery } from "@/__generated__/graphql";
import { Loader } from "@/components/common/Loader.tsx";
import LongContentWrapper from "@/components/common/LongContentWrapper.tsx";
import InviteUsers from "@/components/SettingsSidepanel/Users/InviteUsers.tsx";
import PendingInvitations from "@/components/SettingsSidepanel/Users/PendingInvitations.tsx";
import { Button } from "@/components/ui/button.tsx";
import { useUserContext } from "@/contexts/UserContextProvider.tsx";
import { USERS_QUERY } from "@/graphql/queries/common/UsersQuery.ts";
import { MONTH_DAY_YEAR_FORMAT, userRoles } from "@/helpers/constants.ts";
import { formatDate } from "@/helpers/date.ts";
import { useQuery } from "@apollo/client";
import { ChevronDown, Pencil } from "lucide-react";
import React, { useState } from "react";
import EditUser from "@/components/SettingsSidepanel/Users/EditUser.tsx";
import { UserRoundSearch, Shield, User as UserIcon } from "lucide-react";
import { UserRole } from "@/__generated__/graphql";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { gql } from "@apollo/client";

const PROJECT_OFFICES_QUERY = gql`
  query ProjectOffices {
    projectOffices {
      id
      name
      company {
        id
      }
    }
  }
`;

const userIcons = {
  [UserRole.Admin]: <Shield className="size-4 text-yellow-500" />,
  [UserRole.Collaborator]: <UserIcon className="size-4 text-blue-500" />,
  [UserRole.Reviewer]: <UserRoundSearch className="size-4 text-green-500" />,
};

const UsersTab: React.FC<TabProps> = ({ onRequestClose }) => {
  const [invitations, setInvitations] = useState<User[] | null>(null);
  const [inviteUsersComponent, setInviteUsersComponent] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [officeSearch, setOfficeSearch] = useState("");
  const [selectedOfficeId, setSelectedOfficeId] = useState<string | null>(null);

  const { currentUser } = useUserContext();

  const projectOffices = useQuery<ProjectOfficesQuery>(PROJECT_OFFICES_QUERY);

  const { loading, error, data } = useQuery<UsersQuery>(USERS_QUERY, {
    variables: { officeId: selectedOfficeId },
  });
  if (loading || !data) {
    return (
      <div className="flex items-center h-full">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center py-6">
        Error Loading Users: {error.message}
      </div>
    );
  }
  const pendingUsers: User[] = [];
  const joinedUsers: User[] = [];
  data.users.forEach((user) => {
    if (!user.lastLogin && !user.isActive) {
      pendingUsers.push(user);
    } else {
      joinedUsers.push(user);
    }
  });

  return (
    <div className="flex flex-col h-full">
      {!invitations && !inviteUsersComponent && !selectedUser && (
        <>
          <SettingsSheetHeader onRequestClose={onRequestClose} title="Users" />
          <div className="flex flex-col gap-4 p-4 rounded-2xl border border-accent-foreground overflow-auto">
            <SettingsSheetProfile />
            <Separator />

            <div className="flex justify-between items-center bg-primary">
              <Button
                variant="paint"
                onClick={() => setInviteUsersComponent(true)}
              >
                + Users
              </Button>
              <div className="flex gap-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="flex-1 border justify-between text-secondary-foreground font-normal">
                      {selectedOfficeId
                        ? projectOffices?.data?.projectOffices.find(
                            (o) => o.id === selectedOfficeId,
                          )?.name
                        : "All Offices"}
                      <ChevronDown />
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    className="text-secondary-foreground max-h-44"
                    style={{
                      width: "var(--radix-dropdown-menu-trigger-width)",
                    }}
                    align="end"
                  >
                    <div className="p-2">
                      <input
                        type="text"
                        placeholder="Search office..."
                        className="office-search-input w-full border rounded px-2 py-1 text-sm"
                        value={officeSearch}
                        onChange={(e) => setOfficeSearch(e.target.value)}
                        onKeyDownCapture={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onKeyUpCapture={(e) => e.stopPropagation()}
                        onKeyUp={(e) => e.stopPropagation()}
                      />
                    </div>
                    <DropdownMenuItem onClick={() => setSelectedOfficeId(null)}>
                      All Offices
                    </DropdownMenuItem>
                    {[...(projectOffices?.data?.projectOffices || [])]
                      .filter((office) =>
                        office.name
                          .toLowerCase()
                          .includes(officeSearch.toLowerCase()),
                      )
                      .sort((a, b) =>
                        (a.name || "").localeCompare(b.name || "", undefined, {
                          sensitivity: "base",
                        }),
                      )
                      .map((office) => (
                        <DropdownMenuItem
                          key={office.id}
                          className={cn(
                            "cursor-pointer flex items-center gap-2",
                            selectedOfficeId === office.id &&
                              "bg-accent-foreground",
                          )}
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedOfficeId(office.id);
                          }}
                        >
                          {office.name}
                        </DropdownMenuItem>
                      ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="paint"
                  disabled={pendingUsers.length === 0}
                  onClick={() => setInvitations(pendingUsers)}
                >
                  Pending Invitations: {pendingUsers.length}
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-auto bg-primary">
              <div
                id="users-table-header"
                className="bg-accent text-secondary-foreground sticky top-0 z-10 flex w-full p-4 text-base font-medium"
              >
                <p className="w-1/4">Name</p>
                <p className="w-1/6">Joined on </p>
                <p className="w-1/5">Invite by</p>
                <p className="w-1/5">Role</p>
                <p className="w-1/5">Status</p>
              </div>

              <div id="users-table-body">
                {joinedUsers.map((user: User, index) => (
                  <div
                    key={index}
                    className="border-ring hover:bg-secondary/50 text-foreground flex w-full border-b p-4 text-sm font-light"
                  >
                    <p className="w-1/4 flex items-center gap-1">
                      {userIcons[user.role]}
                      <LongContentWrapper>
                        {user.firstName} {user.lastName}
                      </LongContentWrapper>
                    </p>
                    <p className="w-1/6">
                      {formatDate(user.dateJoined, MONTH_DAY_YEAR_FORMAT)}
                    </p>
                    <p className="w-1/5">
                      <LongContentWrapper>
                        {user.inviter?.firstName} {user.inviter?.lastName}
                      </LongContentWrapper>
                    </p>
                    <p className="w-1/5">{userRoles[user.role]}</p>
                    <p
                      className={cn(
                        "w-1/6",
                        user.isActive ? "text-green-500" : "text-red-500",
                      )}
                    >
                      {user.isActive ? "Active" : "Deactivated"}
                    </p>
                    <p>
                      {currentUser?.id !== user.id && (
                        <Pencil
                          className="size-4 cursor-pointer hover:text-primary-foreground/50"
                          onClick={() => setSelectedUser(user)}
                        />
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {invitations && (
        <PendingInvitations
          invitations={invitations}
          onBack={() => setInvitations(null)}
          onRequestClose={onRequestClose}
        />
      )}

      {inviteUsersComponent && currentUser?.office && (
        <InviteUsers
          onBack={() => setInviteUsersComponent(false)}
          onRequestClose={onRequestClose}
          office={currentUser.office}
          offices={projectOffices.data?.projectOffices || []}
        />
      )}

      {selectedUser && (
        <EditUser
          user={selectedUser}
          onRequestClose={onRequestClose}
          onBack={() => setSelectedUser(null)}
          offices={projectOffices.data?.projectOffices || []}
        />
      )}
    </div>
  );
};

export default UsersTab;
